import { Inject, Injectable } from '@angular/core';
import { get, isNil } from 'lodash';
import { environment } from 'src/environments/environment';
import { PendoConfiguration } from '../pendo.module';

declare const pendo: any;

export enum PendoEvents {
  DiscoveredChapterNav = 'Discovered Chapter Nav'
}

export interface PendoVisitorData {
  visitor: {
    id?: string;
    email?: string;
    full_name?: string;
    purpose?: string;
    role?: string;
    field?: string;
    premium?: boolean;
    developer?: boolean;
  };
  account: {
    id?: string;
    name?: string;
    subscriptionType?: string;
    usersCount?: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class PendoService {
  private appended = false;
  private singleEvents: { [key: string]: boolean} = {};

  constructor(
    @Inject('pendoConfig') private config: PendoConfiguration
  ) { }

  appendScript() {
    if (!this.config.enabled || this.appended) {
      return;
    }

    const script = `
      (function(apiKey){
        (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
        v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
            o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
            y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
            z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
      })('${this.config.pendoId}');
    `;

    const scriptElement = document.createElement('script');
    scriptElement.textContent = script;

    document.body.appendChild(scriptElement);

    this.appended = true;
  }

  initialize(visitorData: PendoVisitorData) {
    const _visitorData = {...visitorData};

    if (!this.pendoEnabled()) {
      return;
    }

    // Assign a unique prefix to visitor ID for different deployment environments
    // so that there are no conflicts between local, QA, staging, and production data
    const prefix = get(this.config, `idPrefixes[${environment.deploy}]`, '');
    _visitorData.visitor.id = `${prefix}${_visitorData.visitor.id}`;
    _visitorData.account.id = `${prefix}${_visitorData.account.id ?? 999999999}`;

    pendo.initialize(visitorData);
  }

  track(trackType: string, metadata?: any) {
    if (this.pendoEnabled()) {
      pendo.track(trackType, metadata);
    }
  }

  trackSingle(trackType: string, metadata?: any) {
    // If the event has already been tracked, don't track again
    if (this.singleEvents[trackType]) {
      return;
    }
    
    if (this.pendoEnabled()) {
      this.track(trackType, metadata);
      this.singleEvents[trackType] = true;
    }
  }

  private pendoEnabled(): boolean {
    if (!this.config.enabled) {
      return false;
    }

    // Limit pendo to specific pages so that we don't blow through our data budget
    // Mostly used by viewer
    const whitelist: string[] = get(this.config, `whitelist[${environment.application}]`, null);
    if (!isNil(whitelist)) {

      // Normally we'd use activatedRoute, but its not initalized at this point, so 
      // we're falling back to native javascript
      const urlParams = new URLSearchParams(window.location.search);
      const id = urlParams.get('id') || urlParams.get('be') || urlParams.get('m');

      return whitelist.includes(id);
    }

    return true;
  }
}
